// Admin config — real settings that affect operations

import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';

export default function AdminConfigPage() {

const API_BASE = window.location.hostname === 'localhost' ? '/api' : 'https://purplemine.mkim0818.workers.dev/api';
  const [tab, setTab] = useState<'fees' | 'cancel' | 'reasons' | 'services'>('fees');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('carp2p_token') || '';
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  // --- Fee Rates ---
  const [feeRates, setFeeRates] = useState<any[]>([]);
  // --- Cancel Policy ---
  const [cancelPolicy, setCancelPolicy] = useState({
    free_cancel_days: 7, late1_days: 6, late1_pct: 10, late2_days: 2, late2_pct: 30, noshow_pct: 100,
    refund_business_hours_only: true, business_hours: '09:00-18:00',
    weekend_next_business_day: true, refund_processing_days: 5, force_majeure_full_refund: true,
  });
  // --- Rejection Reasons ---
  const [rejectionReasons, setRejectionReasons] = useState<any[]>([]);
  const [newReason, setNewReason] = useState('');
  // --- Additional Services ---
  const [services, setServices] = useState<any[]>([]);
  const [newService, setNewService] = useState({ name: '', price: '', unit: 'per_day', type: 'care' });

  useEffect(() => {
    Promise.all([
      fetch(API_BASE + '/admin/config/fee-rates', { headers }).then(r => r.json()),
      fetch(API_BASE + '/admin/config/policies', { headers }).then(r => r.json()),
      fetch(API_BASE + '/admin/config/rejection-reasons', { headers }).then(r => r.json()),
      fetch(API_BASE + '/services', { headers }).then(r => r.json()),
    ]).then(([f, p, r, s]) => {
      setFeeRates(f.fee_rates || []);
      const cp = p.policies?.find((x: any) => x.policy_key === 'cancel_policy');
      if (cp) {
        try { setCancelPolicy(JSON.parse(cp.policy_value)); } catch {}
      }
      setRejectionReasons(r.rejection_reasons || []);
      setServices(s.services || s || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const savePolicy = async (key: string, value: any) => {
    setSaving(true);
    try {
      const policies = await fetch(API_BASE + '/admin/config/policies', { headers }).then(r => r.json());
      const pol = policies.policies?.find((x: any) => x.policy_key === key);
      if (pol) {
        await fetch(`/api/admin/config/policies/${pol.id}`, {
          method: 'PUT', headers, body: JSON.stringify({ policy_value: value }),
        });
      }
      toast.success('저장 완료');
    } catch (err: any) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  const addRejectionReason = async () => {
    if (!newReason.trim()) return;
    setSaving(true);
    try {
      await fetch(API_BASE + '/admin/config/rejection-reasons', {
        method: 'POST', headers, body: JSON.stringify({ reason_text: newReason, category: 'other' }),
      });
      setNewReason('');
      toast.success('추가 완료');
      const r = await fetch(API_BASE + '/admin/config/rejection-reasons', { headers }).then(r => r.json());
      setRejectionReasons(r.rejection_reasons || []);
    } catch (err: any) { toast.error(err.message); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" /></div>;
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900 mb-2">시스템 설정</h1>
      <p className="text-sm text-gray-500 mb-6">플랫폼 운영에 필요한 설정을 관리합니다</p>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-xl p-1 w-fit flex-wrap">
        {[
          { key: 'fees', label: '💰 수수료율' },
          { key: 'cancel', label: '📋 취소 정책' },
          { key: 'reasons', label: '🚫 거절 사유' },
          { key: 'services', label: '🔧 부가서비스' },
        ].map(t => (
          <button key={t.key} onClick={() => setTab(t.key as any)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all focus:outline-none focus:ring-2 focus:ring-brand-500 ${
              tab === t.key ? 'bg-white text-slate-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Fee Rates */}
      {tab === 'fees' && (
        <div className="bg-white rounded-2xl overflow-x-auto border border-gray-100 shadow-sm p-6 max-w-xl">
          <h3 className="font-semibold text-slate-900 mb-2">플랫폼 수수료율</h3>
          <p className="text-xs text-gray-400 mb-4">
            외부 호스트(차주)에게만 적용됩니다. 퍼플마인 자체 차량은 수수료가 면제됩니다.
          </p>
          {feeRates.map((f: any) => (
            <div key={f.id} className="flex items-center justify-between py-3 border-b border-gray-50 text-sm">
              <div>
                <span className="font-medium text-slate-700">{f.rate_name}</span>
                <span className="text-gray-400 ml-2 text-xs">({f.applies_to === 'host' ? '차주 부담' : f.applies_to === 'carrier' ? '캐리어 부담' : '플랫폼 부담'})</span>
              </div>
              <span className="font-semibold text-slate-900">{(f.rate_pct * 100).toFixed(0)}%</span>
            </div>
          ))}
        </div>
      )}

      {/* Cancel Policy */}
      {tab === 'cancel' && (
        <div className="bg-white rounded-2xl overflow-x-auto border border-gray-100 shadow-sm p-6 max-w-xl">
          <h3 className="font-semibold text-slate-900 mb-2">취소 및 환불 규정</h3>
          <p className="text-xs text-gray-400 mb-4">퍼플마인 단가표 기준 환불 규정</p>

          {/* Policy summary — visual */}
          <div className="space-y-2 mb-6">
            {[
              { range: '촬영 7일 전까지', refund: '100% 환불', color: 'bg-emerald-50 border-emerald-200 text-emerald-700' },
              { range: '촬영 6일 ~ 3일 전', refund: '10% 공제 후 환불', color: 'bg-blue-50 border-blue-200 text-blue-700' },
              { range: '촬영 2일 ~ 1일 전', refund: '30% 공제 후 환불', color: 'bg-amber-50 border-amber-200 text-amber-700' },
              { range: '촬영 당일', refund: '환불 불가 (노쇼)', color: 'bg-red-50 border-red-200 text-red-700' },
            ].map(r => (
              <div key={r.range} className={`flex items-center justify-between px-4 py-3 rounded-xl border ${r.color}`}>
                <span className="text-sm font-medium">{r.range}</span>
                <span className="text-sm font-semibold">{r.refund}</span>
              </div>
            ))}
          </div>

          <div className="border-t border-gray-100 pt-4 space-y-3">
            <h4 className="text-sm font-semibold text-slate-700">추가 규정</h4>
            <div className="text-xs text-gray-500 space-y-2 leading-relaxed">
              <p>• 환불 요청은 <strong>평일 업무 시간({cancelPolicy.business_hours})</strong> 내에 접수된 건에 한해 처리됩니다.</p>
              <p>• 주말 및 공휴일에 접수된 환불 요청은 <strong>다음 영업일</strong>에 접수된 것으로 간주합니다.</p>
              <p>• 환불은 <strong>영업일 기준 {cancelPolicy.refund_processing_days}일 이내</strong>에 처리됩니다.</p>
              <p>• 천재지변, 정부 지침 등 <strong>불가항력적 사유</strong>는 증빙서류 확인 후 전액 환불됩니다.</p>
            </div>
          </div>

          {/* Editable fields (collapsible) */}
          <details className="mt-4 border border-gray-200 rounded-xl p-4">
            <summary className="text-xs font-medium text-gray-400 cursor-pointer select-none">⚙️ 정책 수치 수정</summary>
            <div className="mt-4 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">무료 취소 기한 (일)</label>
                  <input type="number" className="input-field text-sm" value={cancelPolicy.free_cancel_days}
                    onChange={e => setCancelPolicy(p => ({...p, free_cancel_days: parseInt(e.target.value) || 0}))} />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">1단계 공제율 (%)</label>
                  <input type="number" className="input-field text-sm" value={cancelPolicy.late1_pct}
                    onChange={e => setCancelPolicy(p => ({...p, late1_pct: parseInt(e.target.value) || 0}))} />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">2단계 공제율 (%)</label>
                  <input type="number" className="input-field text-sm" value={cancelPolicy.late2_pct}
                    onChange={e => setCancelPolicy(p => ({...p, late2_pct: parseInt(e.target.value) || 0}))} />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">환불 처리 기한 (일)</label>
                  <input type="number" className="input-field text-sm" value={cancelPolicy.refund_processing_days}
                    onChange={e => setCancelPolicy(p => ({...p, refund_processing_days: parseInt(e.target.value) || 0}))} />
                </div>
              </div>
              <button onClick={() => savePolicy('cancel_policy', cancelPolicy)} disabled={saving}
                className="px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-brand-500">
                {saving ? '저장 중...' : '정책 수치 저장'}
              </button>
            </div>
          </details>
        </div>
      )}

      {/* Rejection Reasons */}
      {tab === 'reasons' && (
        <div className="bg-white rounded-2xl overflow-x-auto border border-gray-100 shadow-sm p-6 max-w-xl">
          <h3 className="font-semibold text-slate-900 mb-2">거절 사유 관리</h3>
          <p className="text-xs text-gray-400 mb-4">예약 거절 시 선택할 수 있는 사유 목록입니다</p>
          <div className="space-y-1.5 mb-4">
            {rejectionReasons.map((r: any) => (
              <div key={r.id} className="flex items-center gap-2 text-sm py-1.5 px-3 bg-gray-50 rounded-lg">
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 text-gray-500">{r.category}</span>
                <span className="text-slate-700">{r.reason_text}</span>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <input type="text" className="input-field text-sm flex-1"
              placeholder="새 거절 사유 입력" value={newReason}
              onChange={e => setNewReason(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addRejectionReason()} />
            <button onClick={addRejectionReason} disabled={saving || !newReason.trim()}
              className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-brand-500">
              추가
            </button>
          </div>
        </div>
      )}

      {/* Additional Services */}
      {tab === 'services' && (
        <div className="bg-white rounded-2xl overflow-x-auto border border-gray-100 shadow-sm p-6 max-w-xl">
          <h3 className="font-semibold text-slate-900 mb-2">부가서비스 카탈로그</h3>
          <p className="text-xs text-gray-400 mb-4">예약 시 추가할 수 있는 서비스 목록입니다</p>
          <div className="space-y-1.5">
            {services.map((s: any) => (
              <div key={s.id} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg text-sm">
                <div>
                  <span className="font-medium text-slate-700">{s.name}</span>
                  <span className="text-gray-400 ml-2 text-xs">
                    {s.service_type === 'care' ? '케어' : s.service_type === 'transport' ? '운송' : s.service_type === 'shuttle' ? '셔틀' : '기타'}
                  </span>
                </div>
                <div className="text-right">
                  <span className="font-semibold text-slate-700">{s.base_price?.toLocaleString()}원</span>
                  <span className="text-gray-400 ml-1 text-xs">/{s.price_unit === 'per_day' ? '일' : s.price_unit === 'per_trip' ? '회' : '건'}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
